import { PageHeader, SectionLabel, Card, Stat, SampleBanner, Sparkline, BarTrack } from '../components/ui.js';

interface CustomerSlim {
  account_id: string;
  display_name: string;
  plan: 'starter' | 'pro' | 'elite';
  mrr_cents: number;
  cost_cents_30d: number;
  posts_30d: number;
  tenure_months: number;
}

const FLEET: CustomerSlim[] = [
  { account_id: 'glisten-gilbert', display_name: 'Glisten Gilbert', plan: 'pro', mrr_cents: 64900, cost_cents_30d: 4820, posts_30d: 4, tenure_months: 6 },
  { account_id: 'glisten-mesa', display_name: 'Glisten Mesa', plan: 'pro', mrr_cents: 64900, cost_cents_30d: 4710, posts_30d: 4, tenure_months: 6 },
  { account_id: 'glisten-glendale', display_name: 'Glisten Glendale', plan: 'pro', mrr_cents: 64900, cost_cents_30d: 5230, posts_30d: 4, tenure_months: 6 },
];

const MRR_TREND = [0, 0, 0, 0, 0, 1947, 1947, 1947, 1947, 1947, 1947, 1947, 1947, 1947, 1947, 1947, 1947, 1947, 1947, 1947, 1947, 1947, 1947, 1947, 1947, 1947, 1947, 1947, 1947, 1947];
const POSTS_VELOCITY = [3, 4, 4, 5, 4, 4, 4, 5, 4, 4, 5, 4, 4, 4, 5, 4, 4, 4, 5, 4, 5, 4, 4, 5, 4, 4, 4, 5, 4, 4];
const REGRESSIONS_TREND = [2, 1, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0];

export function OperatorPortfolioPage() {
  const totalMrr = FLEET.reduce((s, c) => s + c.mrr_cents, 0) / 100;
  const totalCost30d = FLEET.reduce((s, c) => s + c.cost_cents_30d, 0) / 100;
  const grossMargin = ((totalMrr - totalCost30d) / totalMrr) * 100;
  const avgRevPerCustomer = totalMrr / FLEET.length;
  const totalPosts30d = FLEET.reduce((s, c) => s + c.posts_30d, 0);
  const avgTenure = FLEET.reduce((s, c) => s + c.tenure_months, 0) / FLEET.length;

  const planMix = FLEET.reduce((acc, c) => { acc[c.plan] = (acc[c.plan] ?? 0) + 1; return acc; }, {} as Record<string, number>);

  return (
    <div className="space-y-12">
      <PageHeader
        eyebrow="Operator · Portfolio"
        title={<>How the <em className="text-cream/80">business</em> is doing.</>}
        subtitle="MRR, unit economics, content velocity, and regression rate across the active fleet."
      />
      <SampleBanner note="Sample portfolio metrics. Wires to real cron-produced rows in Phase C." />

      <section>
        <SectionLabel>Revenue</SectionLabel>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat label="MRR" value={`$${totalMrr.toLocaleString()}`} hint={`${FLEET.length} customers`} tone="ok" />
          <Stat label="ARR run-rate" value={`$${(totalMrr * 12).toLocaleString()}`} tone="ok" />
          <Stat label="ARPC" value={`$${avgRevPerCustomer.toFixed(0)}`} hint="per customer / mo" />
          <Stat label="Net new MRR (30d)" value="$0" hint="founding cohort" />
        </div>
      </section>

      <section>
        <SectionLabel>MRR trajectory (30d)</SectionLabel>
        <Card>
          <Sparkline values={MRR_TREND} height={64} color="rgba(110, 231, 183, 0.7)" />
          <div className="mt-3 flex items-center justify-between text-[11px] text-cream/45 tabular-nums">
            <span>30d ago: $0</span>
            <span>today: ${totalMrr.toLocaleString()}</span>
          </div>
        </Card>
      </section>

      <section>
        <SectionLabel>Unit economics</SectionLabel>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat label="Cost / customer / mo" value={`$${(totalCost30d / FLEET.length).toFixed(0)}`} hint="LLM + DataForSEO + storage" />
          <Stat label="Gross margin" value={`${grossMargin.toFixed(1)}%`} tone={grossMargin >= 70 ? 'ok' : 'warn'} />
          <Stat label="Total cost (30d)" value={`$${totalCost30d.toFixed(0)}`} hint="all variable costs" />
          <Stat label="Avg tenure" value={`${avgTenure.toFixed(1)} mo`} />
        </div>
      </section>

      <section>
        <SectionLabel>Plan mix</SectionLabel>
        <Card>
          <div className="space-y-3">
            {(['starter', 'pro', 'elite'] as const).map((plan) => {
              const count = planMix[plan] ?? 0;
              return (
                <div key={plan}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-cream/75 capitalize">{plan}</span>
                    <span className="text-cream tabular-nums">
                      {count}<span className="text-cream/40 text-[11px] ml-1">/ {FLEET.length}</span>
                    </span>
                  </div>
                  <BarTrack value={count} max={FLEET.length} tone={plan === 'pro' ? 'ok' : 'neutral'} />
                </div>
              );
            })}
          </div>
        </Card>
      </section>

      <section>
        <SectionLabel>Engine output (30d)</SectionLabel>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40">Posts published</div>
            <div className="font-serif text-4xl text-cream tracking-tightest mt-1 tabular-nums">{totalPosts30d}</div>
            <div className="mt-3"><Sparkline values={POSTS_VELOCITY} height={40} color="rgba(252, 211, 77, 0.7)" /></div>
            <div className="mt-2 text-[11px] text-cream/45">target: 4/customer · actual: {(totalPosts30d / FLEET.length).toFixed(1)}/customer</div>
          </Card>
          <Card>
            <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40">Regressions detected</div>
            <div className="font-serif text-4xl text-cream tracking-tightest mt-1 tabular-nums">{REGRESSIONS_TREND.reduce((a, b) => a + b, 0)}</div>
            <div className="mt-3"><Sparkline values={REGRESSIONS_TREND} height={40} color="rgba(252, 165, 165, 0.7)" /></div>
            <div className="mt-2 text-[11px] text-cream/45">all auto-fixed within 24h</div>
          </Card>
          <Card>
            <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40">Backups</div>
            <div className="font-serif text-4xl text-cream tracking-tightest mt-1 tabular-nums">90/90</div>
            <div className="mt-3"><Sparkline values={Array.from({ length: 30 }, () => 100)} height={40} color="rgba(110, 231, 183, 0.7)" /></div>
            <div className="mt-2 text-[11px] text-emerald-300/70">100% success rate (30d)</div>
          </Card>
        </div>
      </section>

      <section>
        <SectionLabel>Per-customer breakdown</SectionLabel>
        <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-cream/40 border-b border-cream/[0.06]">
                <th className="px-5 py-3 font-medium">Customer</th>
                <th className="px-3 py-3 font-medium">Plan</th>
                <th className="px-3 py-3 font-medium text-right">MRR</th>
                <th className="px-3 py-3 font-medium text-right">Cost (30d)</th>
                <th className="px-3 py-3 font-medium text-right">Margin</th>
                <th className="px-3 py-3 font-medium text-right">Posts (30d)</th>
                <th className="px-5 py-3 font-medium text-right">Tenure</th>
              </tr>
            </thead>
            <tbody>
              {FLEET.map((c) => {
                const margin = ((c.mrr_cents - c.cost_cents_30d) / c.mrr_cents) * 100;
                return (
                  <tr key={c.account_id} className="border-b border-cream/[0.04] last:border-0 hover:bg-cream/[0.02] transition-colors">
                    <td className="px-5 py-3 text-cream/85">{c.display_name}</td>
                    <td className="px-3 py-3 text-cream/65 capitalize">{c.plan}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-cream/85">${(c.mrr_cents / 100).toFixed(0)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-cream/65">${(c.cost_cents_30d / 100).toFixed(0)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-emerald-300/85">{margin.toFixed(0)}%</td>
                    <td className="px-3 py-3 text-right tabular-nums text-cream/85">{c.posts_30d}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-cream/65">{c.tenure_months} mo</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
